import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

const ENTITY_TYPE_ALIASES = {
  routes: 'route',
  airports: 'airport',
  airlines: 'airline',
  countries: 'country',
  destinations: 'destination',
};

export function normalizeEntityType(entityType) {
  const key = (entityType || '').toLowerCase();
  return ENTITY_TYPE_ALIASES[key] || key;
}

/** Human-readable message from admin SEO API failures. */
export function formatSeoApiError(err) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === 'object') {
    const parts = [];
    if (Array.isArray(detail.errors) && detail.errors.length) {
      parts.push(detail.errors.join('; '));
    } else if (Array.isArray(detail) && detail.length) {
      parts.push(detail.join('; '));
    } else {
      parts.push(JSON.stringify(detail));
    }
    if (Array.isArray(detail.warnings) && detail.warnings.length) {
      parts.push(`Warnings: ${detail.warnings.join('; ')}`);
    }
    return parts.join(' | ') || err.message || 'Request failed';
  }
  return detail || err?.message || 'Request failed';
}

export const fetchSeoHealth = () => api.get('/api/admin/seo/health').then((r) => r.data);
export const fetchSyncStatus = () => api.get('/api/admin/seo/sync/status').then((r) => r.data);
export const fetchLogostreamBreaker = () =>
  api.get('/api/admin/seo/logostream/breaker').then((r) => r.data);
export const bootstrapSeo = () => api.post('/api/admin/seo/bootstrap').then((r) => r.data);
export const syncLogostream = (scope = 'all') =>
  api.post(`/api/admin/seo/sync/logostream?scope=${scope}`).then((r) => r.data);
export const syncVisaAll = () => api.post('/api/admin/seo/visa/sync-all').then((r) => r.data);
// `visaSeedMap` removed — RapidAPI /v2/visa/map is over-quota and not needed.
// The 30 launch countries are synced via syncVisaAll() which uses /v2/visa/check.
export const listSeoPages = (params) => api.get('/api/admin/seo/pages', { params }).then((r) => r.data);
export const getSeoPage = (pageId) => api.get(`/api/admin/seo/pages/${pageId}`).then((r) => r.data);
export const pagesHealth = () => api.get('/api/admin/seo/pages/health').then((r) => r.data);
export const generatePage = (pageType, entityId, publish = false) =>
  api.post(`/api/admin/seo/pages/generate/${pageType}/${entityId}?publish=${publish}`).then((r) => r.data);
export const generateAllPages = (tierMax = 2) =>
  api.post(`/api/admin/seo/pages/generate-all?tier_max=${tierMax}`).then((r) => r.data);
export const approvePage = (pageId) => api.post(`/api/admin/seo/pages/${pageId}/approve`).then((r) => r.data);
export const bulkApprovePages = (params = {}) =>
  api.post('/api/admin/seo/pages/bulk-approve', null, { params }).then((r) => r.data);
export const syncRouteAirlines = (params = {}) =>
  api.post('/api/admin/seo/sync/route-airlines', null, { params }).then((r) => r.data);
export const patchPage = (pageId, body) => api.patch(`/api/admin/seo/pages/${pageId}`, body).then((r) => r.data);
export const bulkUpdatePages = (body) =>
  api.post('/api/admin/seo/pages/bulk-update', body).then((r) => r.data);
export const regeneratePageContent = (pageId, blockKeys) =>
  api.post(`/api/admin/seo/pages/${pageId}/regenerate-content`, { block_keys: blockKeys || null }).then((r) => r.data);
export const recomputeLinks = (publishedOnly = false) =>
  api.post(`/api/admin/seo/links/recompute?published_only=${publishedOnly}`).then((r) => r.data);
export const refreshRouteFares = (routeId) =>
  api.post(`/api/admin/seo/routes/${routeId}/refresh-fares`).then((r) => r.data);
export const refreshTierFares = (tier) =>
  api.post(`/api/admin/seo/fares/refresh-tier/${tier}`).then((r) => r.data);
export const listRoutes = (limit = 100) => api.get('/api/admin/seo/routes', { params: { limit } }).then((r) => r.data);
export const listAirports = (limit = 100) => api.get('/api/admin/seo/airports', { params: { limit } }).then((r) => r.data);

export const fetchSeoDashboard = () => api.get('/api/admin/seo/dashboard').then((r) => r.data);
export const fetchContentAudit = (params) =>
  api.get('/api/admin/seo/pages/content-audit', { params }).then((r) => r.data);
export const fetchPageHealth = (pageId) =>
  api.get(`/api/admin/seo/pages/${pageId}/health`).then((r) => r.data);
export const evaluateIndexing = (pageId) =>
  api.post(`/api/admin/seo/pages/${pageId}/evaluate-indexing`).then((r) => r.data);
export const listSeoEntities = (entityType, params) =>
  api.get(`/api/admin/seo/entities/${normalizeEntityType(entityType)}`, { params }).then((r) => r.data);
export const getSeoEntity = (entityType, entityId) =>
  api.get(`/api/admin/seo/entities/${normalizeEntityType(entityType)}/${entityId}`).then((r) => r.data);
export const fetchSitemapsStatus = () => api.get('/api/admin/seo/sitemaps/status').then((r) => r.data);
export const fetchRouteFares = (routeId) =>
  api.get(`/api/admin/seo/fares/${routeId}`).then((r) => r.data);
export const listVisaRecords = () => api.get('/api/admin/seo/visa').then((r) => r.data);
export const listBaggageRules = (airlineIata) =>
  api.get('/api/admin/seo/baggage', { params: airlineIata ? { airline_iata: airlineIata } : {} }).then((r) => r.data);
export const listAuditLogs = (params) =>
  api.get('/api/admin/seo/audit-logs', { params }).then((r) => r.data);

export const fetchTierManifests = () =>
  api.get('/api/admin/seo/tier-manifests').then((r) => r.data);
export const applyTierManifests = () =>
  api.post('/api/admin/seo/tier-manifests/apply').then((r) => r.data);
export const uploadTierManifests = (overrides) =>
  api.post('/api/admin/seo/tier-manifests/upload', { overrides }).then((r) => r.data);
export const syncCountryLogostream = (iso2 = null, force = false) =>
  api
    .post('/api/admin/seo/sync/country-logostream', null, {
      params: { ...(iso2 ? { iso2 } : {}), force },
    })
    .then((r) => r.data);
export const refreshTierBaggage = (tier) =>
  api.post(`/api/admin/seo/baggage/refresh-tier/${tier}`).then((r) => r.data);
export const warmTier1Caches = () =>
  api.post('/api/admin/seo/launch/warm-tier-1').then((r) => r.data);
export const listEntitiesByTier = (entityType, tier, params = {}) =>
  api
    .get(`/api/admin/seo/entities/${normalizeEntityType(entityType)}/by-tier`, {
      params: { tier, ...params },
    })
    .then((r) => r.data);
export const patchSeoEntity = (entityType, entityId, body) =>
  api
    .patch(`/api/admin/seo/entities/${normalizeEntityType(entityType)}/${entityId}`, body)
    .then((r) => r.data);
export const fetchEntityRaw = (entityType, entityId) =>
  api
    .get(`/api/admin/seo/entities/${normalizeEntityType(entityType)}/${entityId}/raw`)
    .then((r) => r.data);
export const listSeoCronRuns = (params) =>
  api.get('/api/admin/seo/cron-runs', { params }).then((r) => r.data);
export const listSeoApiFailures = (params) =>
  api.get('/api/admin/seo/api-failures', { params }).then((r) => r.data);

/** Hub paths per page_type — matches services/seo/constants.py::MASTER_HUBS. */
const HUB_PATHS = {
  route: '/flights',
  airport: '/airports',
  airline: '/airlines',
  country: '/countries',
  destination: '/destinations',
  visa: '/visa',
};

/** Public-facing preview URL. Opens the page through the React shell so the
 *  admin sees the SAME chrome, search component, header, and footer that
 *  real visitors see — not the bare SSR HTML. */
export const previewPageUrl = (page) => {
  if (!page) return '/';
  const base = API.replace(/\/$/, '');
  if (typeof page === 'string') {
    // Backwards-compat: just an id was passed → fall back to the legacy
    // admin SSR preview.
    return `${base}/api/admin/seo/pages/${page}/preview`;
  }
  const hub = HUB_PATHS[page.page_type];
  if (!hub || !page.slug) {
    return `${base}/api/admin/seo/pages/${page.id}/preview`;
  }
  return `${base}${hub}/${page.slug}`;
};
